import _ from "lodash";
import React, { useState } from "react";
import { connect } from "react-redux";

import classes from "./TableBulkActions.scss";
import { useI18n } from "Hooks";
import { getSelectedCurriculumId } from "modules/CommonSelectors";
import getCurriculumIdsFilterForTags from "Utils/getCurriculumIdsFilterForTags";
import DialogueBox from "UIComponents/DialogueBox";
import {
  bulkUpdateColumnIds,
  onDeleteClick,
  onBulkAddGrades,
  onBulkAddTags,
  onBulkRemoveGrades,
  onBulkRemoveTags,
} from "../GenericTableEditorUtils";
import {
  DeleteOutlined,
  GraduationCapOutlined,
  TagOutlined,
} from "@toddle-edu/ds-icons";
import { colors } from "@toddle-edu/ds-theme";
import { Alert, Button, Dropdown, DropdownMenu } from "@toddle-edu/ds-web";
import { getDeleteDialogueBoxContentBasedOnSetType } from "./TableBulkActionsUtils";
import { AddItemsModal, RemoveItemsModal } from "../commonComponents";

const dialogueBoxStyles = {
  modalBodyStyle: {
    paddingTop: "24px",
    paddingRight: "48px",
  },
  footerContainerStyle: {
    paddingRight: "48px",
  },
};

const TableBulkActions = props => {
  const {
    nodes,
    setNodes,
    selectedStandardsCount,
    selectedNodeIds,
    rootNodeId,
    nodeType,
    allowedBulkActions = [],
    gradeOptions,
    tagOptions,
    createNewTags,
    curriculumProgramId,
  } = props;
  const [isDialogueBoxOpen, setIsDialogueBoxOpen] = useState(false);

  const [selectedBulkAction, setSelectedBulkAction] = useState("");

  const [selectedBulkActionType, setSelectedBulkActionType] = useState("");

  const [itemsList, setItemsList] = useState([]);

  // Here items can be GRADES or TAGS
  const { t } = useI18n(["scopeAndSequence", "common"]);

  const onCreateNewTag = async label => {
    const newTags = await createNewTags([
      {
        label,
        ...getCurriculumIdsFilterForTags({
          curriculumProgramId,
          isMutation: true,
        }),
      },
    ]);
    if (newTags) {
      setItemsList(itemsList => [...itemsList, ...newTags]);
    }
  };

  const getBulkActionConfig = () => {
    const ITEMS_CONFIG = {
      TAGS: {
        ADD: {
          buttonText1: t("common:cancel"),
          buttonText2: t("common:add"),
          modalTitle: t("common:add_with_label", { label: t("common:tags") }),
          searchPlaceholderText: t("scopeAndSequence:enter_a_item", {
            label: t("common:tag"),
          }),
          noItemsAddedText: t("scopeAndSequence:no_item_added", {
            type: t("common:tags"),
          }),
          noItemsText: t("scopeAndSequence:no_item_created", {
            type: t("common:tags"),
          }),
          noItemsSubText: t("scopeAndSequence:go_ahead_tags_desc", {
            msg: t("scopeAndSequence:create_first_tag"),
          }),
          tag: t("common:tag"),
          isCreateItems: true,
          onCreateNewTag: onCreateNewTag,
        },
        REMOVE: {
          buttonText1: t("common:cancel"),
          buttonText2: t("common:save"),
          modalTitle: t("common:remove_with_label", {
            label: t("common:tags"),
          }),
          noItemsText: t("scopeAndSequence:no_item_added", {
            type: t("common:tags"),
          }),
          noItemsSubText: t("scopeAndSequence:go_ahead_tags_desc", {
            msg: t("scopeAndSequence:add_first_tag"),
          }),
          tags: t("common:tags"),
          modalDescription: t(
            "scopeAndSequence:remove_list_items_description",
            {
              item: t("common:tag"),
              items: t("common:tags"),
            }
          ),
        },
      },
      GRADES: {
        ADD: {
          buttonText1: t("common:cancel"),
          buttonText2: t("common:add"),
          modalTitle: t("common:add_with_label", {
            label: t("common:grade_plural"),
          }),
          searchPlaceholderText: t("scopeAndSequence:enter_a_item", {
            label: t("common:grade"),
          }),
          noItemsAddedText: t("scopeAndSequence:no_item_added", {
            type: t("common:grade_plural"),
          }),
          noItemsText: t("scopeAndSequence:no_item_created", {
            type: t("common:grade_plural"),
          }),
          noItemsSubText: t("scopeAndSequence:go_ahead_tags_desc", {
            msg: t("scopeAndSequence:add_first_grade"),
          }),
          tag: t("common:grade_plural"),
          isCreateItems: false,
          onCreateNewTag: null,
        },
        REMOVE: {
          buttonText1: t("common:cancel"),
          buttonText2: t("common:save"),
          modalTitle: t("common:remove_with_label", {
            label: t("common:grade_plural"),
          }),
          noItemsText: t("scopeAndSequence:no_item_added", {
            type: t("common:grade_plural"),
          }),
          noItemsSubText: t("scopeAndSequence:go_ahead_tags_desc", {
            msg: t("scopeAndSequence:add_first_grade"),
          }),
          tags: t("common:grade_plural"),
          modalDescription: t(
            "scopeAndSequence:remove_list_items_description",
            {
              item: t("common:grade"),
              items: t("common:grade_plural"),
            }
          ),
        },
      },
    };

    return _.get(
      ITEMS_CONFIG,
      [selectedBulkAction, selectedBulkActionType],
      {}
    );
  };

  const selectedStandardsText = t("common:label_selected", {
    label: selectedStandardsCount,
  });

  const deleteText = t("common:delete");

  const generateDialogueBoxConfig = () => {
    const {
      modalTitle,
      modalSubtext,
      modalWarning,
    } = getDeleteDialogueBoxContentBasedOnSetType({ nodeType, t });

    return {
      modalTitle,
      modalBody: (
        <div>
          <div
            style={{
              color: `${colors.jaguar}`,
              margin: "0px 0px 24px 0px",
            }}
          >
            {modalSubtext}
          </div>
          <div className={classes.dialogBody}>
            <Alert type="warning" message={modalWarning} />
          </div>
        </div>
      ),
      cancelText: t("common:cancel"),
      confirmText: t("common:delete"),
      dialogContentClass: classes.dialogBoxContent,
    };
  };

  const handleDeleteClick = () => {
    onDeleteClick({
      nodes,
      setNodes,
      selectedNodeIds,
      rootNodeId,
    });
  };

  const toggleDialogueBoxDisplay = () => {
    setIsDialogueBoxOpen(prevState => !prevState);
  };

  const dialogueBoxConfig = generateDialogueBoxConfig();

  const onDropdownItemClick = ({ key, action }) => {
    switch (key) {
      case "ADD_TAGS":
      case "ADD_GRADES": {
        setSelectedBulkAction(action);
        setSelectedBulkActionType("ADD");
        break;
      }
      case "REMOVE_TAGS":
      case "REMOVE_GRADES": {
        setSelectedBulkAction(action);
        setSelectedBulkActionType("REMOVE");
        break;
      }
    }
  };

  const renderAllowedBulkAction = ({ action }) => {
    switch (action) {
      case bulkUpdateColumnIds.grades: {
        return (
          <Dropdown
            getPopupContainer={el => {
              return el;
            }}
            overlay={
              <DropdownMenu
                onClick={({ key }) => onDropdownItemClick({ key, action })}
                options={[
                  {
                    label: t("common:add_with_label", {
                      label: t("common:grade_plural"),
                    }),
                    key: "ADD_GRADES",
                  },
                  {
                    label: t("common:remove_with_label", {
                      label: t("common:grade_plural"),
                    }),
                    key: "REMOVE_GRADES",
                  },
                ]}
                shouldStopPropagation
                placement={"bottomRight"}
              />
            }
          >
            <Button
              icon={<GraduationCapOutlined />}
              variant={"outlined-subtle"}
              size={"small"}
              onClick={e => {
                e.stopPropagation();
              }}
            >
              {t("common:manage_with_label", {
                label: t("common:grade_plural"),
              })}
            </Button>
          </Dropdown>
        );
      }

      case bulkUpdateColumnIds.tags: {
        return (
          <Dropdown
            getPopupContainer={el => {
              return el;
            }}
            overlay={
              <DropdownMenu
                onClick={({ key }) => onDropdownItemClick({ key, action })}
                options={[
                  {
                    label: t("common:add_with_label", {
                      label: t("common:tags"),
                    }),
                    key: "ADD_TAGS",
                  },
                  {
                    label: t("common:remove_with_label", {
                      label: t("common:tags"),
                    }),
                    key: "REMOVE_TAGS",
                  },
                ]}
                shouldStopPropagation
                placement={"bottomRight"}
              />
            }
          >
            <Button
              icon={<TagOutlined />}
              variant={"outlined-subtle"}
              size={"small"}
              onClick={e => {
                e.stopPropagation();
              }}
            >
              {t("common:manage_with_label", { label: t("common:tags") })}
            </Button>
          </Dropdown>
        );
      }

      default: {
        return null;
      }
    }
  };

  const getBulkActionOptions = () => {
    switch (selectedBulkAction) {
      case bulkUpdateColumnIds.grades: {
        return _.map(gradeOptions, grade => {
          return {
            label: grade.name,
            id: grade.id,
          };
        });
      }
      case bulkUpdateColumnIds.tags: {
        return tagOptions;
      }
    }
  };

  const closeItemsModal = () => {
    setSelectedBulkAction("");
    setSelectedBulkActionType("");
    setItemsList([]);
  };

  const addItems = () => {
    switch (selectedBulkAction) {
      case bulkUpdateColumnIds.grades: {
        const newGrades = _.map(itemsList, item => {
          return {
            id: item.id,
            name: item.label,
          };
        });

        onBulkAddGrades({
          nodes,
          setNodes,
          selectedNodeIds,
          itemsList: newGrades,
        });
        break;
      }
      case bulkUpdateColumnIds.tags: {
        onBulkAddTags({
          nodes,
          setNodes,
          selectedNodeIds,
          itemsList,
        });
        break;
      }
    }
    closeItemsModal();
  };

  const removeItems = () => {
    switch (selectedBulkAction) {
      case bulkUpdateColumnIds.grades: {
        onBulkRemoveGrades({
          nodes,
          setNodes,
          selectedNodeIds,
          itemsList,
        });
        break;
      }
      case bulkUpdateColumnIds.tags: {
        onBulkRemoveTags({
          nodes,
          setNodes,
          selectedNodeIds,
          itemsList,
        });
        break;
      }
    }
    closeItemsModal();
  };

  const getCumulativeGradesList = () => {
    const grades = _.uniqBy(
      _.flatMap(selectedNodeIds, nodeId => nodes[nodeId].grades),
      "id"
    );

    const gradeOptionsByKey = _.keyBy(gradeOptions, "id");

    return _.map(grades, ({ id }) => ({
      id,
      label: _.get(gradeOptionsByKey[id], "name", ""),
    }));
  };

  const getCumulativeTagsList = () => {
    const tags = _.uniqBy(
      _.flatMap(selectedNodeIds, nodeId => nodes[nodeId].tags),
      "id"
    );

    const tagOptionsByKey = _.keyBy(tagOptions, "id");

    return _.map(tags, ({ id }) => {
      return { id: id, label: tagOptionsByKey[id].label };
    });
  };

  const getCumulativeList = () => {
    switch (selectedBulkAction) {
      case bulkUpdateColumnIds.grades: {
        return getCumulativeGradesList();
      }
      case bulkUpdateColumnIds.tags: {
        return getCumulativeTagsList();
      }
    }
  };

  const bulkActionConfig = getBulkActionConfig();

  return (
    <div className={classes.container}>
      <div className={classes.selectedStandards}>{selectedStandardsText}</div>
      <div className={classes.actionsContainer}>
        {_.map(allowedBulkActions, action => {
          return (
            <React.Fragment key={action}>
              {renderAllowedBulkAction({ action })}
            </React.Fragment>
          );
        })}
        <Button
          variant={"outlined-subtle"}
          size={"small"}
          icon={<DeleteOutlined />}
          onClick={toggleDialogueBoxDisplay}
        >
          {deleteText}
        </Button>
      </div>
      {isDialogueBoxOpen && (
        <DialogueBox
          modalTitle={dialogueBoxConfig.modalTitle}
          showModal={true}
          onClickButton2={handleDeleteClick}
          modalBody={dialogueBoxConfig.modalBody}
          toggleDialogueBoxDisplay={toggleDialogueBoxDisplay}
          button1={dialogueBoxConfig.cancelText}
          button2={dialogueBoxConfig.confirmText}
          modalBodyStyle={dialogueBoxStyles.modalBodyStyle}
          footerContainerStyle={dialogueBoxStyles.footerContainerStyle}
          customModalContentClass={dialogueBoxConfig.dialogContentClass}
        />
      )}
      {selectedBulkActionType === "ADD" ? (
        <AddItemsModal
          items={getBulkActionOptions()}
          onClickButton1={closeItemsModal}
          onClickButton2={addItems}
          buttonText1={bulkActionConfig.buttonText1}
          buttonText2={bulkActionConfig.buttonText2}
          modalTitle={bulkActionConfig.modalTitle}
          searchPlaceholderText={bulkActionConfig.searchPlaceholderText}
          noItemsAddedText={bulkActionConfig.noItemsAddedText}
          noItemsText={bulkActionConfig.noItemsText}
          noItemsSubText={bulkActionConfig.noItemsSubText}
          isCreateItems={bulkActionConfig.isCreateItems}
          onCreateItemClick={bulkActionConfig.onCreateNewTag}
          itemName={bulkActionConfig.itemName}
          selectedItemsList={itemsList}
          setSelectedItemsList={setItemsList}
        />
      ) : null}
      {selectedBulkActionType === "REMOVE" ? (
        <RemoveItemsModal
          items={getCumulativeList()}
          onClickButton1={closeItemsModal}
          onClickButton2={removeItems}
          buttonText1={bulkActionConfig.buttonText1}
          buttonText2={bulkActionConfig.buttonText2}
          modalTitle={bulkActionConfig.modalTitle}
          noItemsText={bulkActionConfig.noItemsText}
          noItemsSubText={bulkActionConfig.noItemsSubText}
          itemName={bulkActionConfig.itemName}
          removedItemsList={itemsList}
          setRemovedItemsList={setItemsList}
          modalDescription={bulkActionConfig.modalDescription}
        />
      ) : null}
    </div>
  );
};

const mapStateToProps = state => {
  const curriculumProgramId = getSelectedCurriculumId(state);
  return {
    curriculumProgramId,
  };
};

export default connect(mapStateToProps, null)(TableBulkActions);
